import React, { useState, useEffect } from 'react';
import { Star, Award, MessageCircle, TrendingUp, User } from 'lucide-react';

// ✅ IMPORTACIONES CON RUTAS ABSOLUTAS
import { useGlobal } from 'context/GlobalContext';

const BitacoraConducta = ({ ninos }) => {
  // 1. Consumimos las alertas globales para mostrar los mensajes reales del maestro
  const { alertasAdmin } = useGlobal();

  // 2. Estado para el niño seleccionado
  const [ninoActivo, setNinoActivo] = useState(ninos[0] || null);

  useEffect(() => {
    if (!ninoActivo && ninos.length > 0) {
      setNinoActivo(ninos[0]);
    }
  }, [ninos, ninoActivo]);

  // 3. Filtrar el historial específico de este niño que viene del Admin/Maestro
  const historialReal = alertasAdmin.filter(alerta => alerta.nino === ninoActivo?.nombre);

  if (!ninoActivo) return (
    <div className="p-10 text-center opacity-50 font-black uppercase">Cargando datos de exploradores...</div>
  );

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
      
      {/* TÍTULO DE LA SECCIÓN */}
      <div className="text-center space-y-2">
        <h2 className="text-3xl font-black text-slate-900 italic tracking-tight uppercase">Bitácora de Conducta</h2>
        <p className="text-sm text-slate-500 font-medium italic">Refuerzo positivo y seguimiento en tiempo real</p>
      </div>

      {/* SELECTOR DE NIÑOS */}
      <div className="flex gap-3 overflow-x-auto hide-scrollbar pb-2">
        {ninos.map((nino) => (
          <button
            key={nino.id}
            onClick={() => setNinoActivo(nino)}
            className={`flex items-center gap-3 px-6 py-4 rounded-[2rem] whitespace-nowrap transition-all flex-1 justify-center border-2 ${
              ninoActivo.id === nino.id
                ? 'bg-indigo-50 border-indigo-500 text-indigo-900 shadow-md scale-100'
                : 'bg-white border-transparent text-slate-500 shadow-sm hover:bg-slate-50 scale-95'
            }`}
          >
            <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${
              ninoActivo.id === nino.id ? 'bg-indigo-500 text-white' : 'bg-slate-200 text-slate-400'
            }`}>
              <User size={20} />
            </div>
            <span className="text-lg font-black tracking-tight uppercase italic">{nino.nombre.split(' ')[0]}</span>
          </button>
        ))}
      </div>

      {/* PANEL DE ESTRELLAS */}
      <div className="bg-gradient-to-br from-amber-400 to-orange-500 p-8 rounded-[3rem] text-white shadow-xl shadow-amber-200/50 relative overflow-hidden">
        <Star className="absolute -right-8 -top-8 opacity-20 rotate-12" size={180} fill="white" />
        
        <p className="text-[10px] font-black uppercase tracking-[0.3em] opacity-90 mb-2">
          Estrellas de {ninoActivo.nombre}
        </p>
        
        <div className="flex items-baseline gap-2 mb-4">
          <h3 className="text-6xl font-black italic tracking-tighter">{ninoActivo.estrellas || 0}</h3>
          <span className="text-xl font-bold opacity-80 uppercase">pts</span>
        </div>

        <div className="bg-white/20 backdrop-blur-md rounded-2xl p-4 border border-white/30 flex items-start gap-3">
          <Award size={24} className="shrink-0 text-amber-100" />
          <p className="text-sm font-bold leading-snug text-left italic">
            {ninoActivo.estrellas >= 10 
              ? '¡Increíble! Ha superado la meta. Celebra este logro en casa.' 
              : `¡Va súper bien! Faltan ${15 - (ninoActivo.estrellas || 0)} estrellas para su próxima recompensa.`}
          </p>
        </div>
      </div>

      {/* HISTORIAL RECIENTE */}
      <div className="space-y-5">
        <h4 className="flex items-center gap-2 font-black italic text-slate-800 uppercase text-sm tracking-widest ml-2">
          <TrendingUp size={18} className="text-indigo-500" /> Reportes del Salón
        </h4>
        
        <div className="space-y-4">
          {historialReal.length > 0 ? (
            historialReal.map((item) => (
              <div 
                key={item.id} 
                className={`p-6 rounded-[2.5rem] border flex gap-5 hover:shadow-md transition-all ${
                  item.tipo === 'positivo' ? 'bg-emerald-50 border-emerald-100' : 'bg-white border-slate-100 shadow-sm'
                }`}
              >
                <div className={`w-14 h-14 rounded-2xl flex items-center justify-center shrink-0 shadow-inner ${
                  item.tipo === 'positivo' ? 'bg-emerald-500 text-white' : 'bg-slate-100 text-slate-500'
                }`}>
                  {item.tipo === 'positivo' ? <Star size={24} fill="currentColor" /> : <MessageCircle size={24} />}
                </div>
                
                <div className="flex-1 text-left">
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    <span className="text-[9px] font-black uppercase tracking-widest text-slate-400 bg-white px-2 py-1 rounded-md border border-slate-100">
                      {item.hora}
                    </span>
                    <span className="text-[9px] font-black uppercase tracking-widest text-indigo-500 italic">
                      {item.salon}
                    </span>
                  </div>
                  <p className="text-base text-slate-700 font-bold leading-relaxed italic">
                    "{item.reporteMaestro}"
                  </p>
                </div>
              </div>
            ))
          ) : (
            <div className="p-10 text-center bg-slate-50 rounded-[2.5rem] border-2 border-dashed border-slate-200">
               <p className="text-xs font-black text-slate-400 uppercase tracking-widest">Sin reportes en esta sesión</p>
            </div>
          )}
        </div>
      </div>

    </div>
  );
};

export default BitacoraConducta;